import React, { useEffect } from 'react';
import { useMapState } from 'rooks';
import {
  BOARD_DATE_FORMAT,
  BOARD_PERIOD_TYPES,
  BOARD_SEARCH_CONDITIONS,
} from '@common/utils/board';
import { format } from 'date-fns';
import SatDatePicker from '@components/common/satUI/SatDatePicker';

const todayDateString = $utils.formatBoardDate(new Date());
const initialSearchInfo = {
  startDd: '',
  endDd: '',
  searchName: '',
  searchCond: '',
  bbsClssCd: '',
};

const BoardSearch = ({
  boardCode,
  postTypes,
  pagingInfo,
  setPagingInfo,
  renewParams,
}) => {
  const { error: toastError } = $hooks.useToast();
  const [searchInfo, { set: setSearchInfo, setMultiple: changeSearchInfo }] =
    useMapState(initialSearchInfo);

  const checkValid = searchInfo => {
    const { startDd, endDd } = searchInfo;
    const isDateSelected = startDd && endDd;
    const isValidPeriod = $utils.checkValidPeriod(startDd, endDd);

    if (isDateSelected && !isValidPeriod) {
      toastError('시작 날짜가 종료 날짜보다 뒤로 설정 될 수는 없습니다.');
      return false;
    }

    return true;
  };

  const handleSearch = () => {
    const isValidSearch = checkValid(searchInfo);
    if (!isValidSearch) return;

    const defaultParams = $utils.getBoardDefaultParams(boardCode, {
      ...pagingInfo,
      pageIndex: 0,
    });
    const searchParams = $utils.filterValid(searchInfo);
    const params = { ...defaultParams, ...searchParams };

    renewParams(params);
    setPagingInfo('pageIndex', 0);
  };

  const handleChange = ({ target }) => {
    const { name, value } = target;
    setSearchInfo(name, value);
  };

  const handleSelectPeriod = periodType => {
    const periodInfo = {
      startDd: searchInfo.startDd,
      endDd: todayDateString,
    };

    // 연간/월간은 해당 연/월/일의 최초 날짜로 계산
    const { year, month, sundayDate } = $utils.getTodayInfo();
    if (periodType === 'year') {
      periodInfo['startDd'] = format(
        new Date(`${year}-01-01`),
        BOARD_DATE_FORMAT,
      );
    } else if (periodType === 'month') {
      periodInfo['startDd'] = format(
        new Date(`${year}-${month}-01`),
        BOARD_DATE_FORMAT,
      );
    } else if (periodType === 'week') {
      periodInfo['startDd'] = format(sundayDate, BOARD_DATE_FORMAT);
    } else if (periodType === 'day') {
      periodInfo['startDd'] = todayDateString;
    }

    changeSearchInfo({ ...searchInfo, ...periodInfo });
  };

  useEffect(() => {
    if (postTypes.length === 1)
      setSearchInfo('bbsClssCd', postTypes[0]?.bbsClssCd);
  }, []);

  return (
    <div className="search-box">
      <div className="data-group-left">
        <div className="data-area">
          <span className="data-tit">기간 검색</span>
          <SatDatePicker
            size={'small'}
            value={searchInfo.startDd}
            disabledDates={[null, initialSearchInfo.endDd]}
            onChange={v => setSearchInfo('startDd', v)}
          />

          <span className="tilde">~</span>

          <SatDatePicker
            size={'small'}
            value={searchInfo.endDd}
            disabledDates={[null, initialSearchInfo.endDd]}
            onChange={v => setSearchInfo('endDd', v)}
          />

          {BOARD_PERIOD_TYPES.map(({ name, value }) => (
            <button
              key={value}
              className={$classNames(`btn-${value}`)}
              onClick={() => handleSelectPeriod(value)}
            >
              {name}
            </button>
          ))}
        </div>
      </div>

      {/* 자료구분이 1개이면 선택 창 보이지 않음 */}
      {postTypes.length > 1 && (
        <div className="data-group-left">
          <div className="data-area">
            <span className="data-tit">자료 구분</span>
            <div className="sel-group">
              <select name="bbsClssCd" onChange={handleChange}>
                <option value="">전체</option>

                {postTypes.map(({ bbsClssNm, bbsClssCd }, index) => (
                  <option key={index} value={bbsClssCd}>
                    {bbsClssNm}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      <div className="data-group-left">
        <div className="data-area">
          <span className="data-tit">직접 입력</span>
          <div className="sel-group">
            <select name="searchCond" onChange={handleChange}>
              <option value="">전체</option>

              {BOARD_SEARCH_CONDITIONS.map(({ name, value }) => (
                <option key={value} value={value}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          <div className="input-wrap input-search wide2">
            <input
              type="text"
              id="text1"
              className="input"
              placeholder="검색어를 입력하세요."
              value={searchInfo.searchName}
              onChange={e => setSearchInfo('searchName', e.target.value)}
            />
          </div>
        </div>
      </div>

      <button className="btn-search" onClick={handleSearch}>
        조회
      </button>
    </div>
  );
};

BoardSearch.propTypes = {
  boardCode: $propTypes.string,
  postTypes: $propTypes.array,
  pagingInfo: $propTypes.object,
  setPagingInfo: $propTypes.func,
  renewParams: $propTypes.func,
};

export default BoardSearch;
